import { useEffect, useState } from 'react';

interface Pt {
  x: number;
  y: number;
}

const OLT: Pt = { x: 230, y: 330 };
const ODCS: Array<Pt & { feeder: string }> = [
  { x: 560, y: 230, feeder: 'M230 330 L400 330 L400 240 L560 230' },
  { x: 520, y: 640, feeder: 'M230 330 L300 330 L330 610 L520 640' },
  { x: 1010, y: 380, feeder: 'M230 330 C420 360 620 430 760 410 S920 380 1010 380' },
];
/** ODPs with their ODC (index into ODCS) and the houses they feed (offsets from the ODP). */
const ODPS: Array<Pt & { odc: number; homes: Pt[] }> = [
  { x: 770, y: 160, odc: 0, homes: [o(-46, -34), o(0, -54), o(48, -30)] },
  { x: 610, y: 80, odc: 0, homes: [o(-52, 10), o(52, -6)] },
  { x: 800, y: 740, odc: 1, homes: [o(-50, 34), o(0, 56), o(54, 30)] },
  { x: 400, y: 820, odc: 1, homes: [o(-54, 20), o(-10, 58), o(46, 40)] },
  { x: 1240, y: 290, odc: 2, homes: [o(-40, -50), o(16, -60), o(62, -24)] },
  { x: 1190, y: 580, odc: 2, homes: [o(-56, 30), o(0, 58), o(56, 34)] },
  { x: 1460, y: 430, odc: 2, homes: [o(-30, -56), o(40, -40), o(30, 50)] },
];

function o(x: number, y: number): Pt {
  return { x, y };
}

/** Distribution cable from an ODC to an ODP: along x first, then y (like a street). */
const elbow = (a: Pt, b: Pt) => `M${a.x} ${a.y} L${b.x} ${a.y} L${b.x} ${b.y}`;

function usePrefersReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const on = () => setReduced(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduced;
}

/**
 * Decorative background for the sign-in pages: a stylised map (land, river, parks, roads) with an
 * FTTH network on top, OLT → ODC → ODP → homes, and light pulses running along the fibre. Colours
 * come from the map and status tokens, so it follows the dark/light theme. No animation when the
 * user prefers reduced motion.
 */
export function NetworkMapBackdrop() {
  const reduced = usePrefersReducedMotion();
  return (
    <div className="fn-netmap" aria-hidden>
      <svg viewBox="0 0 1600 1000" preserveAspectRatio="xMidYMid slice" width="100%" height="100%">
        <rect width="1600" height="1000" fill="var(--map-land)" />
        <g className="fn-netmap__grid">
          {Array.from({ length: 17 }, (_, i) => (
            <line key={`v${i}`} x1={i * 100} y1="0" x2={i * 100} y2="1000" />
          ))}
          {Array.from({ length: 11 }, (_, i) => (
            <line key={`h${i}`} x1="0" y1={i * 100} x2="1600" y2={i * 100} />
          ))}
        </g>

        <g fill="var(--map-park)">
          <path d="M1180 470 C1260 430 1360 450 1380 520 C1400 590 1310 640 1230 620 C1150 600 1120 510 1180 470Z" />
          <path d="M120 760 C200 720 300 760 290 840 C280 910 170 930 120 880 C80 840 70 790 120 760Z" />
          <path d="M900 60 C980 30 1080 60 1070 120 C1060 180 960 190 910 160 C870 135 860 80 900 60Z" />
        </g>

        <path
          d="M-20 560 C180 520 340 700 600 690 S980 820 1240 760 S1520 700 1640 730"
          fill="none"
          stroke="var(--map-water)"
          strokeWidth="30"
          strokeLinecap="round"
        />

        <g fill="none" stroke="var(--map-road)" strokeWidth="7" strokeLinecap="round">
          <path d="M0 180 L1600 140" />
          <path d="M0 880 L1600 840" />
          <path d="M300 0 L360 1000" />
          <path d="M1100 0 L1060 1000" />
          <path d="M1360 0 L1410 1000" />
          <path d="M0 660 L640 640" />
        </g>
        <g fill="none" stroke="var(--map-road-major)" strokeWidth="13" strokeLinecap="round">
          <path d="M-20 420 C300 380 600 470 900 400 S1400 330 1640 380" />
          <path d="M700 -20 C680 250 760 520 720 1020" />
        </g>

        {/* Drop cables to homes */}
        <g className="fn-netmap__drop">
          {ODPS.flatMap((p, i) =>
            p.homes.map((h, j) => (
              <line key={`d${i}-${j}`} x1={p.x} y1={p.y} x2={p.x + h.x} y2={p.y + h.y} />
            )),
          )}
        </g>
        {/* Distribution cables ODC → ODP */}
        <g className="fn-netmap__cable fn-netmap__cable--dist">
          {ODPS.map((p, i) => (
            <path key={`c${i}`} d={elbow(ODCS[p.odc]!, p)} />
          ))}
        </g>
        {/* Feeder cables OLT → ODC */}
        <g className="fn-netmap__cable fn-netmap__cable--feeder">
          {ODCS.map((c, i) => (
            <path key={`f${i}`} d={c.feeder} />
          ))}
        </g>
        {/* Light running towards the customers */}
        <g className="fn-netmap__flow">
          {ODCS.map((c, i) => (
            <path key={`ff${i}`} d={c.feeder} style={{ animationDelay: `${i * -0.7}s` }} />
          ))}
          {ODPS.map((p, i) => (
            <path key={`fd${i}`} d={elbow(ODCS[p.odc]!, p)} style={{ animationDelay: `${i * -0.45}s` }} />
          ))}
        </g>

        {!reduced &&
          ODCS.map((c, i) => (
            <circle key={`pk${i}`} r="5" className="fn-netmap__packet">
              <animateMotion
                dur={`${4 + i}s`}
                repeatCount="indefinite"
                begin={`${i * 0.8}s`}
                path={c.feeder}
              />
            </circle>
          ))}

        {/* Homes */}
        <g className="fn-netmap__home">
          {ODPS.flatMap((p, i) =>
            p.homes.map((h, j) => (
              <path
                key={`h${i}-${j}`}
                transform={`translate(${p.x + h.x} ${p.y + h.y})`}
                d="M-8 2 L0 -6 L8 2 L8 9 L-8 9Z"
              />
            )),
          )}
        </g>
        {/* ODPs */}
        <g>
          {ODPS.map((p, i) => (
            <g key={`p${i}`} transform={`translate(${p.x} ${p.y})`}>
              <circle r="18" className="fn-netmap__halo" style={{ animationDelay: `${i * 0.6}s` }} />
              <circle r="8" className="fn-netmap__odp" />
            </g>
          ))}
        </g>
        {/* ODCs */}
        <g>
          {ODCS.map((c, i) => (
            <rect
              key={`o${i}`}
              x={c.x - 12}
              y={c.y - 12}
              width="24"
              height="24"
              rx="6"
              className="fn-netmap__odc"
            />
          ))}
        </g>
        {/* OLT */}
        <g transform={`translate(${OLT.x} ${OLT.y})`}>
          <circle r="34" className="fn-netmap__halo fn-netmap__halo--olt" />
          <rect x="-20" y="-20" width="40" height="40" rx="9" className="fn-netmap__olt" />
          <path d="M-10 -8 H10 M-10 0 H10 M-10 8 H10" className="fn-netmap__olt-lines" />
        </g>

        <g className="fn-netmap__label">
          <text x={OLT.x} y={OLT.y + 46}>
            OLT
          </text>
          <text x={ODCS[0]!.x} y={ODCS[0]!.y + 32}>
            ODC
          </text>
          <text x={ODCS[2]!.x} y={ODCS[2]!.y + 32}>
            ODC
          </text>
          <text x={ODPS[4]!.x} y={ODPS[4]!.y + 30}>
            ODP
          </text>
          <text x={ODPS[2]!.x} y={ODPS[2]!.y - 18}>
            ODP
          </text>
        </g>
      </svg>
      <div className="fn-netmap__shade" />
    </div>
  );
}
